/**
 * Centralized notification dispatcher.
 *
 * Call this from any module to send a notification to a user.
 * It will:
 *  1. Check the recipient's notification preferences
 *  2. Write to community_notifications (in-app bell)
 *  3. Call the Cloud Function to send FCM push (if user has push tokens)
 *
 * Usage:
 *   import { notify } from "../Config/db/notify";
 *   await notify({
 *     recipientId: "userId123",
 *     category: "meetings",          // matches NOTIFICATION_CATEGORIES id
 *     title: "New Meeting Scheduled",
 *     body: "Annual general meeting on 15th March",
 *     data: { meetingId: "abc", url: "/invitations" },
 *   });
 */
import { getNotificationPrefs, getUserTokens } from "./notification.operations";
import { db } from "../firebase";
import { doc, collection, setDoc, getDocs, query, where, serverTimestamp, Timestamp } from "firebase/firestore";

const NOTIFICATION_TABLE = "community_notifications";

/**
 * Send a notification to a single user.
 */
export const notify = async ({ recipientId, category, title, body, data }) => {
  if (!recipientId || !title) return;

  // Check if user has this category enabled
  const prefs = await getNotificationPrefs(recipientId);
  if (prefs[category] === false) return; // user opted out

  // 1. In-app notification (Firestore — shows in bell)
  const notifRef = doc(collection(db, NOTIFICATION_TABLE));
  await setDoc(notifRef, {
    userId: recipientId,
    type: category || "general",
    title,
    body: body || "",
    data: data || {},
    read: false,
    createdAt: Timestamp.now(),
  });

  // 2. Queue push notification (written to a collection that Cloud Functions can trigger on)
  try {
    const tokens = await getUserTokens(recipientId);
    if (tokens.length > 0) {
      const pushRef = doc(collection(db, "push_notification_queue"));
      await setDoc(pushRef, {
        tokens,
        notification: { title, body: body || "" },
        data: { ...(data || {}), type: category || "general" },
        createdAt: serverTimestamp(),
      });
    }
  } catch (err) {
    console.error("Push queue write failed:", err);
    // Non-blocking — in-app notification was already sent
  }
};

/**
 * Send a notification to multiple users.
 */
export const notifyMany = async ({ recipientIds, category, title, body, data }) => {
  if (!recipientIds?.length) return;
  await Promise.allSettled(
    recipientIds.map((id) => notify({ recipientId: id, category, title, body, data }))
  );
};

/**
 * Get all active member userIds for a community.
 */
export const getCommunityMemberIds = async (communityCode) => {
  const snap = await getDocs(
    query(
      collection(db, "representators"),
      where("communityCode", "==", communityCode),
      where("active", "==", true)
    )
  );
  const ids = new Set();
  snap.forEach((d) => {
    const userId = d.data().userId;
    if (userId) ids.add(userId);
  });
  return [...ids];
};

/**
 * Notify all community members (except excludeId).
 */
export const notifyCommunity = async ({ communityCode, excludeId, category, title, body, data }) => {
  const memberIds = await getCommunityMemberIds(communityCode);
  const recipients = excludeId ? memberIds.filter((id) => id !== excludeId) : memberIds;
  return notifyMany({ recipientIds: recipients, category, title, body, data });
};
